// src/App.tsx
import React, { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import Login from './components/Auth/Login';
import Navbar from './components/Navbar/navbar';
import { getVisibleRoutes } from './router';
import { Provider } from 'react-redux';
import store from './redux/store';
import { setNavigateCallback } from './fetchWrapper';
import ErrorBoundary from './components/ErrorBoundary';
import Loader from './components/loader';
import { AuthProvider, useAuth } from './context/AuthContext';

interface PrivateRouteProps {
  element: React.ReactElement;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setNavigateCallback((path) => {
      console.log('Navigating to:', path);
      if (path === '/login' || path === '/logout') {
        logout();
        navigate('/login');
        return;
      }
      navigate(path);
    });
  }, [navigate, logout]);

  if (!user) {
    // Redirect to login if the user is not logged in
    return <Navigate to="/login" />;
  }

  // Render the protected route
  return element;
};

const AppRoutes: React.FC = () => {
  const { user, hasPermission, logout, refreshPermissions } = useAuth();

  // Picks up permission changes an admin makes elsewhere in the app without
  // requiring other logged-in users to log out and back in.
  useEffect(() => {
    if (user) {
      refreshPermissions();
    }
  }, []); // eslint-disable-line

  const visibleRoutes = useMemo(
    () => getVisibleRoutes(hasPermission, user?.role),
    [hasPermission, user?.role]
  );

  const handleLogin = () => {
    // Login.tsx already updates AuthContext directly via useAuth().login()
    // before calling this — nothing further needed here.
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <Routes>
      {/* Define the login route */}
      <Route path="/login" element={<Login onLogin={handleLogin} />} />

      {/* Use a layout for protected routes */}
      <Route
        path="/*"
        element={
          <PrivateRoute
            element={
              <>
                <Navbar onLogout={handleLogout} />
                <Routes>
                  {visibleRoutes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  ))}
                  {/* Add more routes as needed */}
                </Routes>
              </>
            }
          />
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AuthProvider>
        <ErrorBoundary>
          <React.Suspense fallback={<Loader loading={true} />}>
            <AppRoutes />
          </React.Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </Provider>
  );
};

export default App;
